// eslint-disable-next-line no-restricted-imports
import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  Flex,
  TextInput,
  Textarea,
  createForm,
  useId,
  useToasts,
} from '@applyboard/crystal-ui';
import { useEventTracker } from '@applyboard/ui-utils';
import {
  RequestInformation as RequestInformationPayload,
  useRequestInformation,
} from '../../data/requestInformation';
import { RequestInformation } from './types';

interface ExternalDialogControl {
  open: boolean;
  setOpen: (open: boolean) => void;
}

interface RequestInformationProps {
  name: string;
  email: string;
  rpId?: string;
  service: string;
  vendor: string;
  showTriggerButton?: boolean;
  externalDialogControl?: ExternalDialogControl;
}

const { Field, Form } = createForm<RequestInformation>();

export function RequestInformationDialog({
  name,
  email,
  rpId,
  service,
  vendor,
  externalDialogControl,
}: RequestInformationProps) {
  const serviceAllCaps = service?.toUpperCase();
  const vendorAllCaps = vendor?.toUpperCase();
  const defaultFormValues: RequestInformation = {
    name,
    email,
    rpId,
    service: serviceAllCaps,
    vendor: vendorAllCaps,
    inquiryDetails: '',
  };
  const formId = useId();
  const [internalOpen, setInternalOpen] = useState(externalDialogControl?.open || false);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const { trackEvent } = useEventTracker();
  const toasts = useToasts();
  const { requestInformation } = useRequestInformation();
  const isExternallyControlled =
    externalDialogControl?.open !== undefined && externalDialogControl?.setOpen;

  const setDialogState = useCallback(
    (state: boolean) => {
      if (isExternallyControlled) {
        externalDialogControl?.setOpen?.(state);
      }
      setInternalOpen(state);
    },
    [isExternallyControlled, externalDialogControl, setInternalOpen],
  );

  const submitCallback = useCallback(
    (values: RequestInformation) => {
      trackEvent({
        event: 'VAS - Vas Hub Page - Request Information submitted',
        data: { rp_id: rpId, service, vendor },
      });

      setSubmitDisabled(true);

      const requestInformationPayload: RequestInformationPayload = {
        name: values.name,
        email: values.email,
        rp_id: values.rpId,
        service: values.service,
        vendor: values.vendor,
        inquiry_details: values.inquiryDetails,
      };

      requestInformation(
        requestInformationPayload,
        () => {
          setSubmitDisabled(false);
          trackEvent({
            event: 'VAS - Vas Hub Page - Request Information submitted successfully',
            data: { rp_id: rpId, service, vendor, inquiry: values.inquiryDetails },
          });
          toasts.positive(
            `Request for more information for ${vendorAllCaps} ${serviceAllCaps} submitted successfully`,
          );
          setDialogState(false);
        },
        error => {
          console.error('Failed to submit request for more information', error);
          toasts.negative(new Error('Failed to submit request for information'));
          setSubmitDisabled(false);
        },
      );
    },
    [
      trackEvent,
      rpId,
      service,
      vendor,
      requestInformation,
      toasts,
      vendorAllCaps,
      serviceAllCaps,
      setDialogState,
    ],
  );

  useEffect(() => {
    if (isExternallyControlled) {
      setInternalOpen(externalDialogControl.open);
    }
  }, [isExternallyControlled, externalDialogControl?.open]);

  return (
    <Dialog open={internalOpen} size="md" onOpenChange={(state: boolean) => setDialogState(state)}>
      {!isExternallyControlled && (
        <Dialog.Trigger>
          <Button
            aria-label="messages"
            size="md"
            intent="primary"
            emphasis="highlighted"
            width="fill"
            disabled={submitDisabled}
            onClick={() => {
              setInternalOpen(true);
              trackEvent({
                event: 'VAS - Vas Hub Page -  Request Information button clicked',
                data: { rp_id: rpId, service, vendor },
              });
            }}
          >
            Request Info
          </Button>
        </Dialog.Trigger>
      )}
      <Dialog.Heading
        disabled={false}
      >{`Request Information for ${vendorAllCaps} ${serviceAllCaps}`}</Dialog.Heading>
      <Dialog.Content>
        <Form id={formId} defaultValues={defaultFormValues} onSubmit={submitCallback}>
          <Flex align="stretch" direction="column" gap={{ lg: 8, sm: 4, xs: 2 }}>
            <Field as={TextInput} name="name" label="Name" required="Your name is required" />
            <Field as={TextInput} name="email" label="Email" required="Email is required" />
            <Field
              as={Textarea}
              name="inquiryDetails"
              label="Inquiry Details"
              required="Inquiry details is required"
            />
          </Flex>
        </Form>
      </Dialog.Content>
      <Dialog.Actions>
        <Dialog.CloseButton width="fill">Cancel</Dialog.CloseButton>
        <Button intent="primary" width="fill" type="submit" form={formId} disabled={submitDisabled}>
          Submit
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
}
